import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { getTheme } from "@/lib/theme";
import { GoogleButton } from "@/components/GoogleButton";
import { AmbientBackground } from "@/components/AmbientBackground";

export default function SignIn() {
  const { signIn, selectedBusinessId } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const theme = getTheme(selectedBusinessId);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await signIn(email.trim(), password);
      toast.success(`Welcome back, ${u.name}`);
      const from = loc.state?.from;
      if (u.role === "baker") nav("/manage");
      else nav(from || "/shop");
    } catch (err) {
      toast.error(err.message || "Sign in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative">
      <AmbientBackground />
      <div className="relative z-10 min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 hero-gradient">
        <Link to="/" className="font-display text-2xl" style={{ color: "var(--hero-fg)" }}>
          {theme.name}
        </Link>
        <div>
          <h2 className="font-display text-4xl leading-tight" style={{ color: "var(--hero-fg)" }}>
            Welcome back to the table.
          </h2>
          <p className="mt-3 max-w-sm" style={{ color: "color-mix(in srgb, var(--hero-fg) 75%, transparent)" }}>
            {theme.tagline}
          </p>
        </div>
        <div className="text-xs" style={{ color: "color-mix(in srgb, var(--hero-fg) 60%, transparent)" }}>
          {theme.kind} · Order online
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="signin-form">
          <h1 className="font-display text-3xl" style={{ color: "var(--ink)" }}>
            Sign in
          </h1>
          <p className="mt-1 text-sm" style={{ color: "var(--muted)" }}>
            Track orders, pay balances, and reorder faster.
          </p>

          <label className="block mt-6 text-sm font-semibold" style={{ color: "var(--ink)" }}>
            Email
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="signin-email"
              className="mt-1.5 w-full rounded-xl px-4 py-3 outline-none transition focus:ring-2"
              style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
              placeholder="you@email.com"
            />
          </label>
          <label className="block mt-4 text-sm font-semibold" style={{ color: "var(--ink)" }}>
            Password
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="signin-password"
              className="mt-1.5 w-full rounded-xl px-4 py-3 outline-none transition focus:ring-2"
              style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
              placeholder="••••••••"
            />
          </label>

          <div className="mt-2 text-right">
            <Link to="/forgot-password" className="text-xs font-semibold" style={{ color: "var(--brand)" }} data-testid="link-forgot-password">
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="signin-submit"
            className="btn-brand w-full mt-6 rounded-xl py-3 font-semibold disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>

          <div className="flex items-center gap-3 my-5">
            <span className="h-px flex-1" style={{ background: "var(--border)" }} />
            <span className="text-xs" style={{ color: "var(--muted)" }}>or</span>
            <span className="h-px flex-1" style={{ background: "var(--border)" }} />
          </div>
          <GoogleButton label="Sign in with Google" />

          <p className="mt-5 text-sm text-center" style={{ color: "var(--muted)" }}>
            New here?{" "}
            <Link to="/sign-up" className="font-semibold" style={{ color: "var(--brand)" }} data-testid="link-signup">
              Create an account
            </Link>
          </p>
          <p className="mt-3 text-center">
            <Link to="/shop" className="text-xs underline" style={{ color: "var(--muted)" }}>
              Continue browsing as guest
            </Link>
          </p>
        </form>
      </div>
      </div>
    </div>
  );
}
